'use client'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import SectionWrapper from '@/components/ui/SectionWrapper'
import SectionHeader from '@/components/ui/SectionHeader'
import MotionFadeIn from '@/components/ui/MotionFadeIn'
import { Media } from '@/components/Media'
import type { Media as MediaType } from '@/payload-types'

export interface ProjectItem {
  id: string
  title: string
  description: string
  image: MediaType | string
  tags: string[]
  link: string
  slug?: string
}

export interface MoreProjectSectionProps {
  projects: ProjectItem[]
  infoTagText: string
  title: string
  sourceBlock?: string
}

export function MoreProjectCard({
  project,
  sourceBlock,
}: {
  project: ProjectItem
  sourceBlock?: string
}) {
  const imageResource =
    typeof project.image === 'object' && project.image !== null && 'id' in project.image
      ? (project.image as MediaType)
      : null

  return (
    <Link
      href={project.link}
      className="flex flex-col gap-4 group"
      data-card-type="project"
      data-card-slug={project.slug || project.link.split('/').pop()}
      data-card-block={sourceBlock || 'moreProjectSection'}
    >
      <div className="relative h-72 w-full overflow-hidden">
        {imageResource ? (
          <div className="absolute inset-0 transition-transform duration-700 ease-out group-hover:scale-[1.04]">
            <Media
              resource={imageResource}
              fill
              imgClassName="object-cover"
              size="(min-width: 1024px) 384px, 100vw"
              alt={imageResource.alt || project.title}
            />
          </div>
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
            <span className="text-gray-400">Kein Bild verfügbar</span>
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between">
          <div className="flex gap-2">
            {project.tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="bg-brand text-white text-sm font-metropolis font-medium px-2 py-1 uppercase"
              >
                {tag}
              </span>
            ))}
          </div>

          <div className="w-12 h-12 bg-brand flex items-center justify-center">
            <ArrowUpRight className="w-6 h-6 text-white transition-transform duration-200 rotate-45 group-hover:rotate-90" />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="title-lg text-primary uppercase">{project.title}</h4>
        <p className="caption-lg text-secondary">{project.description}</p>
      </div>

      <span className="inline-flex items-center gap-2 font-ui font-medium text-sm text-text-brand group-hover:text-brand/80 transition-colors duration-300">
        <span className="uppercase">Zum Projekt</span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          className="transition-transform duration-300 group-hover:translate-x-1"
        >
          <path
            d="M9 18L15 12L9 6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </Link>
  )
}

export default function MoreProjectSection({
  projects,
  infoTagText,
  title,
  sourceBlock,
}: MoreProjectSectionProps) {
  // Show only first 3 projects
  const visible = projects.slice(0, 3)

  if (visible.length === 0) return null

  return (
    <SectionWrapper background="white" padding="lg">
      <MotionFadeIn initialY={24}>
        <SectionHeader
          infoTagText={infoTagText}
          title={title}
          align="left"
          titleClassName="uppercase"
          subtitleClassName="text-text-secondary"
        />
      </MotionFadeIn>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {visible.map((project, index) => (
          <MotionFadeIn key={project.id} delay={index * 0.1} initialY={24}>
            <MoreProjectCard project={project} sourceBlock={sourceBlock} />
          </MotionFadeIn>
        ))}
      </div>
    </SectionWrapper>
  )
}
